import _ from 'lodash'
import { setExplicitConceptStatuses, unshiftHistoryEntries } from './formSlice'
import { AnyConceptStatus, AnyConceptStatusesByCid, ConceptStatusHistory, ConceptValueDataType, ImportResultLocation, ImportWriteMode, SuperconceptStatus } from '@fusion-advantage/backcompat'
import { Concept, ConceptsByCid, ConceptStatusUpdate, isSuperconceptStatus, timestamp, DiagnosisReviewConcept, cidForDirItemId, CollaboratingPhysicianSignatureNameConcept, AdhocDxConcept } from '@fusion-advantage/formlib'
import { selectForm } from './selectForm'
import { FormThunk, FormRootState } from './formStore'

export const ERR_CONCEPT_NOT_FOUND = (cid: string) => `Concept ${cid} not found`
export const ERR_EXPECTED_INTEGER = (cid: string) => `Expected integer for ${cid}`

export interface ConceptStatusUpdateProcessResult {
	newStatuses: AnyConceptStatus[],
	newHistoryEntries: ConceptStatusHistory[],
}

export class StatusUpdateProcessor {
	userId: string
	location: ImportResultLocation
	concepts: ConceptsByCid
	existingStatuses: AnyConceptStatusesByCid
	newStatuses: AnyConceptStatusesByCid = {}
	newHistoryEntries: ConceptStatusHistory[] = []
	updatedAt: string

	constructor(userId: string|null, location: string|null, concepts: ConceptsByCid, existingStatuses: AnyConceptStatusesByCid) {
		this.userId = userId ?? ''
		this.location = location
		this.concepts = concepts
		this.existingStatuses = existingStatuses
		this.updatedAt = timestamp()
	}

	getConcept(cid: string): Concept {
		const concept = this.concepts[cid]
		if(!concept) {
			debugger
			throw new Error(ERR_CONCEPT_NOT_FOUND(cid))
		}
		return concept
	}

	getConceptStatus(cid: string): AnyConceptStatus|null {
		if(this.newStatuses[cid]) {
			return this.newStatuses[cid]
		}
		if(this.existingStatuses[cid]) {
			return _.cloneDeep(this.existingStatuses[cid])
		}
		return null
	}

	processUpdates(updates: ConceptStatusUpdate[]): ConceptStatusUpdateProcessResult {
		let newUpdates: ConceptStatusUpdate[] = []
		for(const update of updates) {
			const { retainExplicit, negativeUpdates } = this.getNegativeSubconceptUpdates(update)
			if(retainExplicit) {
				newUpdates.push(update)
			}
			newUpdates.push(...negativeUpdates)
		}
		this.updatedAt = timestamp()
		_.forEach(newUpdates, update => {
			this.validate(update)
			if(update.subconceptCid) {
				this.processSubconceptUpdate(update)
			} else {
				this.processConceptUpdate(update)
			}
		})
		return {
			newStatuses: _.values(this.newStatuses),
			newHistoryEntries: _.reverse(this.newHistoryEntries),
		}
	}

	// when a radio button or select subconcept gets set to true,
	// we update the others to false
	// when a radio button or select subconcept gets set to null,
	// we update the others to null
	getNegativeSubconceptUpdates(update: ConceptStatusUpdate): {negativeUpdates: ConceptStatusUpdate[], retainExplicit: boolean} {
		let negativeUpdates: ConceptStatusUpdate[] = []
		let retainExplicit = true
		const concept = this.getConcept(update.cid)
		if(!update.subconceptCid && update.value === null && concept.subconcepts.length > 0) {
			// drop explicit update and generate null subconcept statuses
			retainExplicit = false
		} else if(!update.subconceptCid) {
			// nothing to do
			return {
				negativeUpdates,
				retainExplicit,
			}
		}
		let negativeValue
		if(update.value === true) {
			negativeValue = false
		} else if(update.value === null) {
			negativeValue = null
		} else {
			// no negative values for this update value
			return {
				negativeUpdates,
				retainExplicit,
			}
		}
		switch(concept.valueDataType) {
			case ConceptValueDataType.Radios:
			case ConceptValueDataType.Select:
				for(let conceptSubconceptCid of concept.subconcepts) {
					if(conceptSubconceptCid === update.subconceptCid) {
						continue
					}
					negativeUpdates.push({
						cid: update.cid,
						subconceptCid: conceptSubconceptCid,
						value: negativeValue,
						valueType: update.valueType,
					})
				}
		}
		return {
			negativeUpdates,
			retainExplicit,
		}
	}

	validate(update: ConceptStatusUpdate) {
		const concept = this.getConcept(update.cid)
		if(update.value === '') {
			update.value = null
			return
		}
		if(update.value === null) {
			return
		}
		switch(concept.valueDataType) {
			case ConceptValueDataType.Integer:
				if(update.value === null || _.isInteger(update.value)) {
					return
				}
				if(_.isString(update.value)) {
					update.value = parseInt(update.value)
				}
				if(_.isInteger(update.value)) {
					return
				}
				throw new Error(ERR_EXPECTED_INTEGER(update.cid))
		}
	}

	private processConceptUpdate(update: ConceptStatusUpdate) {
		this.newStatuses[update.cid] = {
			cid: update.cid,
			value: update.value,
			valueType: update.valueType,
			updatedAt: this.updatedAt,
			userId: this.userId,
			location: this.location,
		}
		this.newHistoryEntries.push({
			cid: update.cid,
			subconceptCid: null,
			value: update.value,
			valueType: update.valueType,
			updatedAt: this.updatedAt,
			userId: this.userId,
			location: this.location,
		})
	}

	private processSubconceptUpdate(update: ConceptStatusUpdate) {
		const existingStatus = this.getConceptStatus(update.cid)
		let superStatus: SuperconceptStatus
		if(existingStatus) {
			if(!isSuperconceptStatus(existingStatus)) {
				throw new Error(`expected SuperconceptStatus for Concept ${update.cid}`)
			}
			superStatus = {
				...existingStatus,
				updatedAt: this.updatedAt,
				userId: this.userId,
				location: this.location,
			}
		} else {
			superStatus = {
				cid: update.cid,
				subconcepts: {},
				updatedAt: this.updatedAt,
				userId: this.userId,
				location: this.location,
			}
		}
		superStatus.subconcepts[update.subconceptCid!] = {
			cid: update.subconceptCid!,
			value: update.value,
			valueType: update.valueType,
			updatedAt: this.updatedAt,
			userId: this.userId,
			location: this.location,
		}
		this.newStatuses[superStatus.cid] = superStatus
		this.newHistoryEntries.push({
			cid: update.cid,
			subconceptCid: update.subconceptCid!,
			value: update.value,
			valueType: update.valueType,
			updatedAt: this.updatedAt,
			userId: this.userId,
			location: this.location,
		})
	}
}

export const getStatusUpdateProcessor = (existingStatuses?: AnyConceptStatusesByCid): FormThunk<StatusUpdateProcessor> => (dispatch, getState) => {
	const state = getState()
	const { concepts } = selectForm(state)
	return new StatusUpdateProcessor(
		state.render.authedIdentity?.upn ?? null,
		state.render.importLocation,
		concepts,
		existingStatuses ?? state.form.conceptStatus,
	)
}

export const submitStatusUpdates = (updates: ConceptStatusUpdate[]): FormThunk<void> => (dispatch, getState) => {
	const writableCids = getWritableCids(getState())
	if(writableCids !== 'all') {
		const [writable, nonWritable] = _.partition(updates, x => writableCids.includes(x.cid))
		if(nonWritable.length > 0) {
			console.debug('[submitStatusUpdates] dropping non-writable updates', nonWritable)
		}
		updates = writable
	}
	const processor = dispatch(getStatusUpdateProcessor())
	const { newStatuses, newHistoryEntries } = processor.processUpdates(updates)
	dispatch(setExplicitConceptStatuses(newStatuses))
	dispatch(unshiftHistoryEntries(newHistoryEntries))
}

const getWritableCids = (state: FormRootState): string[]|'all' => {
	const writeMode = state.render.form?.writeMode
	switch(writeMode) {
		case ImportWriteMode.WriteAll:
			return 'all'
		case ImportWriteMode.WriteAuditReview:
			// Sprint 2025-s06 [ISSUE-1195] Ability for auditors to change
			// forms in order to faciliate temporary workarounds is removed
			// return 'all'
			return [
				DiagnosisReviewConcept.cid,
				AdhocDxConcept.cid,
				// we need to allow dir id 391 to be writable in order for the signature wipe to work on audit -> clinician
				cidForDirItemId(391),
				/* per ticket 1449, do not allow general notes to be edited by auditors
				GeneralNotesConcept.cid,
				*/
			]
		case ImportWriteMode.WriteCollaboratingPhysician:
				// only allow collaborating physicians to write to their appropriate fields:
				// signature and notes
				return [
					cidForDirItemId(393),
					cidForDirItemId(1329),
					CollaboratingPhysicianSignatureNameConcept.cid,
				]
		default:
			return []
	}
}
